import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ExternalLink, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { getPageBaseUrl } from "./pageEditorUtils";

interface PreviewTabProps {
  onSave?: () => void;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
  route?: string;
  refreshTrigger?: number;
}

export function PreviewTab({
  onSave,
  isSaving,
  hasUnsavedChanges,
  route,
  refreshTrigger,
}: PreviewTabProps) {
  const [customDomainHostname, setCustomDomainHostname] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetch("/api/custom-domains")
      .then((res) => res.json())
      .then((data) => {
        const domains = data.customDomains || [];
        setCustomDomainHostname(domains[0]?.hostname ?? null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (refreshTrigger === undefined || refreshTrigger === 0) return;
    setRefreshKey((key) => key + 1);
  }, [refreshTrigger]);

  const previewUrl = useMemo(
    () => `${getPageBaseUrl(customDomainHostname)}${route || "/"}`,
    [customDomainHostname, route],
  );

  const handleSave = () => {
    onSave?.();
    setRefreshKey((key) => key + 1);
    toast.success("Saved");
  };

  return (
    <div className="flex-1 min-h-0 overflow-hidden relative min-w-0">
      <iframe
        key={`${previewUrl}-${refreshKey}`}
        title="Page preview"
        src={previewUrl}
        className="w-full h-full border-0 bg-white"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        {route !== undefined && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            asChild
            className="h-8 w-8"
            title="Open page in new tab"
          >
            <a
              href={`${getPageBaseUrl(customDomainHostname)}${route || "/"}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        )}
        {hasUnsavedChanges && onSave && (
          <Button
            type="button"
            size="icon"
            onClick={handleSave}
            disabled={isSaving}
            className="shadow-lg"
            title="Save"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
